import clsx from "clsx";
import React, { Dispatch, FC, SetStateAction } from "react";

import { areFiltersEmpty } from "../../../utils/products";
import { Button } from "../../buttons/button";
import { Icon } from "../../icon";
import { ProductFiltersPanel, ProductFiltersPanelProps } from "./product-filters-panel";
import { ProductFiltersSummary } from "./product-filters-summary";
import { ProductFinderFiltersFormTypes } from "./types";

export interface ProductFinderHeaderProps {
  appliedFilters: ProductFinderFiltersFormTypes;
  specializations: Queries.MachineSpecializationDataFragment[];
  labels: {
    "product-finder-header-filter": string;
  } & ProductFiltersPanelProps["labels"];
  isFiltersPanelOpen: boolean;
  setFiltersPanelOpen: Dispatch<SetStateAction<boolean>>;
  setAppliedFilters: Dispatch<SetStateAction<ProductFinderFiltersFormTypes>>;
}

export const ProductFinderHeader: FC<ProductFinderHeaderProps> = ({
  labels,
  specializations,
  isFiltersPanelOpen,
  setFiltersPanelOpen,
  appliedFilters,
  setAppliedFilters,
}) => {
  const isSingleSpecialization = specializations.length === 1;

  const shouldShowSummary =
    (!isSingleSpecialization && Object.values(appliedFilters.materials).some(Boolean)) ||
    (!isSingleSpecialization && Boolean(appliedFilters.machiningTypeId)) ||
    !areFiltersEmpty({ techSheetFieldsSlugs: appliedFilters.techSheetFieldsSlugs });

  return !specializations.length ? (
    // This code will never be shown in final build as
    // no ProductFinder can be saved without a specialization,
    // it was inserted to inform the editor that something is missing
    <div className="mx-auto text-4xl">
      Careful! No specialization has been inserted in contentful preview.
    </div>
  ) : (
    <div className="flex flex-col gap-2">
      <div>
        <Button
          type="button"
          variant="primary"
          size="sm"
          className="flex gap-1 items-center"
          onClick={() => setFiltersPanelOpen((isOpen) => !isOpen)}
        >
          {labels["product-finder-header-filter"]}
          <Icon icon="filter" className="text-light w-5 pt-1/2" />
        </Button>
      </div>

      <div className="overflow-y-hidden w-full">
        <ProductFiltersPanel
          isOpen={isFiltersPanelOpen}
          setOpen={setFiltersPanelOpen}
          specializations={specializations}
          labels={labels}
        />
      </div>

      <div
        className={clsx(
          "duration-300 transition-all overflow-y-hidden",
          shouldShowSummary ? "max-h-[100vh]" : "max-h-0"
        )}
      >
        <ProductFiltersSummary
          appliedFilters={appliedFilters}
          labels={labels}
          specializations={specializations}
          setAppliedFilters={setAppliedFilters}
        />
      </div>
    </div>
  );
};
